// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { isPlanLimitError } from '@/account/utilities/is-plan-limit-error';
import { handleAPIError } from '@/api/error-utils';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import {
  FilterOption,
  FilterToggleGroup,
} from '@/components/filter-toggle-group';
import { Link } from '@/components/link';
import { SupportSnackbar } from '@/components/support-snackbar';
import {
  CheckCircle,
  ChecklistRtlOutlined,
  KeyboardReturnOutlined,
  SwapHorizOutlined,
} from '@/constants/hardfin-standard-icons';
import { toTransportReferenceDetailsRequest } from '@/constants/shipping-carrier';
import { useToast } from '@/contexts/toast-context';
import { createExchangeOrder } from '@/exchange-order/api/create-exchange-order';
import {
  CreateExchangeOrderRequest,
  DetailedExchangeOrder,
} from '@/exchange-order/api/types';
import { exchangeOrdersQueryKey } from '@/exchange-order/query-keys';
import { createFulfillmentOrder } from '@/fulfillment/api/create-fulfillment-order';
import { FulfillmentOrderForm } from '@/fulfillment/components/fulfillment-order-form';
import { useFulfillmentFileUploads } from '@/fulfillment/hooks/use-fulfillment-file-uploads';
import { fulfillmentsQueryKey } from '@/fulfillment/query-keys';
import { FulfillmentOrderFormData } from '@/fulfillment/schemas/fulfillment-order-form-schema';
import { OrderNumberCopyButton } from '@/order/components/order-number-copy-button';
import { projectFulfillmentBySkuQueryKey } from '@/project/hooks/use-project-fulfillment-by-sku';
import { createReturnOrder } from '@/return-order/api/create-return-order';
import { ReturnOrderForm } from '@/return-order/components/return-order-form';
import { useReturnOrderFileUploads } from '@/return-order/hooks/use-return-order-file-uploads';
import { returnOrdersQueryKey } from '@/return-order/query-keys';
import { ReturnOrderFormData } from '@/return-order/schemas/return-order-form-schema';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { captureException } from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

/**
 * The available order types for creation.
 */
type OrderType = 'fulfill' | 'return' | 'exchange';

interface Props {
  /**
   * Whether the dialog is open.
   */
  isOpen: boolean;

  /**
   * Callback fired when the dialog is closed.
   */
  onClose: () => void;

  /**
   * Optional initial project ID to set the project field for fulfillment orders.
   */
  initialProjectID?: string;

  /**
   * The default order type to show when the dialog opens.
   * Allows listing pages to default to their respective type.
   */
  defaultOrderType?: OrderType;

  /**
   * When true, locks the project field on the fulfillment form. Used when
   * the dialog is opened from a project-scoped entry point so the user
   * can't accidentally retarget the order.
   */
  disableProjectChange?: boolean;
}

const TITLES: Record<OrderType, string> = {
  fulfill: 'Add fulfillment order',
  return: 'Add return order',
  exchange: 'Add exchange',
};

const ORDER_TYPE_OPTIONS: FilterOption<OrderType>[] = [
  {
    value: 'fulfill',
    label: 'Fulfill only',
    icon: <ChecklistRtlOutlined fontSize="small" />,
  },
  {
    value: 'return',
    label: 'Return only',
    icon: <KeyboardReturnOutlined fontSize="small" />,
  },
  {
    value: 'exchange',
    label: 'Exchange',
    icon: <SwapHorizOutlined fontSize="small" />,
  },
];

const FORM_IDS = {
  fulfill: 'create-order-fulfillment-form',
  return: 'create-order-return-form',
  exchangeReturn: 'create-order-exchange-return-form',
  exchangeFulfillment: 'create-order-exchange-fulfillment-form',
};

const EXCHANGE_STEPS = ['Fulfillment order', 'Return order', 'Review'];

/**
 * Unified dialog for creating fulfillment, return, or exchange orders.
 * Features a toggle bar to switch between order types, with state reset
 * on type change to ensure clean form state.
 */
export function CreateOrderDialog(props: Props) {
  const defaultOrderType = props.defaultOrderType ?? 'fulfill';

  // Order type state
  const [orderType, setOrderType] = useState<OrderType>(defaultOrderType);
  const [resetKey, setResetKey] = useState(0);

  // Exchange wizard state
  const [exchangeStep, setExchangeStep] = useState(0);
  const [returnOrderData, setReturnOrderData] =
    useState<ReturnOrderFormData | null>(null);
  const [fulfillmentOrderData, setFulfillmentOrderData] =
    useState<FulfillmentOrderFormData | null>(null);
  // Initial RO data derived from FO - only set once when first entering RO step
  const [initialReturnOrderData, setInitialReturnOrderData] =
    useState<ReturnOrderFormData | null>(null);
  // Key to force RO form remount when initial data changes
  const [roFormKey, setRoFormKey] = useState(0);
  // Store created exchange order for confirmation step
  const [createdExchangeOrder, setCreatedExchangeOrder] =
    useState<DetailedExchangeOrder | null>(null);

  // Error state
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // True while the fulfillment form's selected project is still loading, so we
  // block submission until the manifest reflects the chosen project.
  const [isFulfillmentContextBlocked, setIsFulfillmentContextBlocked] =
    useState(false);

  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { user } = useCurrentUser();
  const fulfillmentFileUploads = useFulfillmentFileUploads();
  const returnOrderFileUploads = useReturnOrderFileUploads();

  // Fulfillment order mutation
  const createFulfillmentMutation = useMutation({
    mutationFn: createFulfillmentOrder,
    onSuccess: (fulfillment) => {
      queryClient.invalidateQueries({
        queryKey: fulfillmentsQueryKey,
        refetchType: 'active',
      });
      // Invalidate the project's fulfillment-by-SKU summary so the next
      // open of this dialog (or the project Fulfillment-by-SKU card)
      // reflects the new FO's quantities in Requested / Fulfilled / Need.
      if (fulfillment.project) {
        queryClient.invalidateQueries({
          queryKey: projectFulfillmentBySkuQueryKey(fulfillment.project.key),
        });
      }
      showToast({
        element: (
          <Typography>
            Fulfillment order{' '}
            <Link
              to="/fulfillments/$fulfillmentKey"
              params={{ fulfillmentKey: fulfillment.key }}
              color="primary.main"
              sx={{ fontWeight: 'bold', textDecoration: 'underline' }}
            >
              {fulfillment.fulfillmentOrderNumber}
            </Link>{' '}
            created
          </Typography>
        ),
        severity: 'success',
        duration: 3000,
      });
      props.onClose();
    },
    onError: (error) => {
      if (isPlanLimitError(error)) {
        return;
      }

      const apiError = handleAPIError(error);
      if (apiError.apiStatusCode > 0) {
        setErrorMessage(apiError.message);
        setShowSnackbar(false);
      } else {
        if (!apiError.sentToSentry) {
          captureException(error);
        }
        setErrorMessage(null);
        setShowSnackbar(true);
      }
    },
  });

  // Return order mutation
  const createReturnMutation = useMutation({
    mutationFn: createReturnOrder,
    onSuccess: (returnOrder) => {
      queryClient.invalidateQueries({
        queryKey: returnOrdersQueryKey,
        refetchType: 'active',
      });
      showToast({
        element: (
          <Typography>
            Return order{' '}
            <Link
              to="/return-orders/$returnOrderKey"
              params={{ returnOrderKey: returnOrder.key }}
              color="primary.main"
              sx={{ fontWeight: 'bold', textDecoration: 'underline' }}
            >
              {returnOrder.returnOrderNumber}
            </Link>{' '}
            created
          </Typography>
        ),
        severity: 'success',
        duration: 3000,
      });
      props.onClose();
    },
    onError: (error) => {
      if (isPlanLimitError(error)) {
        return;
      }

      const apiError = handleAPIError(error);
      if (apiError.apiStatusCode > 0) {
        setErrorMessage(apiError.message);
        setShowSnackbar(false);
      } else {
        if (!apiError.sentToSentry) {
          captureException(error);
        }
        setErrorMessage(null);
        setShowSnackbar(true);
      }
    },
  });

  // Exchange order mutation
  const createExchangeMutation = useMutation({
    mutationFn: (request: CreateExchangeOrderRequest) =>
      createExchangeOrder(request),
    onSuccess: async (exchangeOrder) => {
      await queryClient.invalidateQueries({ queryKey: exchangeOrdersQueryKey });
      await queryClient.invalidateQueries({ queryKey: returnOrdersQueryKey });
      await queryClient.invalidateQueries({ queryKey: fulfillmentsQueryKey });

      // The exchange order's nested FO/RO change project demand coverage.
      // The detailed exchange-order response doesn't surface the project key
      // directly, so fall back to the dialog's initial project context.
      if (props.initialProjectID) {
        await queryClient.invalidateQueries({
          queryKey: projectFulfillmentBySkuQueryKey(props.initialProjectID),
        });
      }

      // Store the created exchange order and move to confirmation step
      setCreatedExchangeOrder(exchangeOrder);
      setExchangeStep(3); // Move to confirmation step
    },
    onError: (error) => {
      if (isPlanLimitError(error)) {
        return;
      }

      const apiError = handleAPIError(error);
      if (!apiError.sentToSentry) {
        captureException(error);
      }
      setErrorMessage(null);
      setShowSnackbar(true);
    },
  });

  const isPending =
    createFulfillmentMutation.isPending ||
    createReturnMutation.isPending ||
    createExchangeMutation.isPending;

  // Reset all state after dialog close animation completes
  const handleAfterClose = useCallback(() => {
    setOrderType(defaultOrderType);
    setResetKey((k) => k + 1);
    setExchangeStep(0);
    setReturnOrderData(null);
    setFulfillmentOrderData(null);
    setInitialReturnOrderData(null);
    setRoFormKey(0);
    setCreatedExchangeOrder(null);
    setErrorMessage(null);
    setShowSnackbar(false);
    setIsFulfillmentContextBlocked(false);
    createFulfillmentMutation.reset();
    createReturnMutation.reset();
    createExchangeMutation.reset();
    fulfillmentFileUploads.reset();
    returnOrderFileUploads.reset();
  }, [
    defaultOrderType,
    createFulfillmentMutation,
    createReturnMutation,
    createExchangeMutation,
    fulfillmentFileUploads,
    returnOrderFileUploads,
  ]);

  // Handle order type change - reset forms
  const handleOrderTypeChange = useCallback(
    (newType: OrderType) => {
      setOrderType(newType);
      setResetKey((k) => k + 1);
      setExchangeStep(0);
      setReturnOrderData(null);
      setFulfillmentOrderData(null);
      setInitialReturnOrderData(null);
      setRoFormKey(0);
      setErrorMessage(null);
      setIsFulfillmentContextBlocked(false);
      fulfillmentFileUploads.reset();
      returnOrderFileUploads.reset();
    },
    [fulfillmentFileUploads, returnOrderFileUploads]
  );

  // Handle fulfillment form submission
  const handleFulfillmentSubmit = useCallback(
    (data: FulfillmentOrderFormData) => {
      const items = data.items.map((item) => ({
        itemId: item.item?.id ?? '',
        requestedQuantity: item.quantity ?? 0,
      }));

      createFulfillmentMutation.mutate({
        projectId: data.project?.id ?? null,
        fromLocationId: data.fromLocation?.id ?? '',
        toLocationId: data.toLocation?.id ?? '',
        promiseShipDate: data.promiseShipDate,
        promiseDeliveryDate: data.promiseDeliveryDate,
        orderNotes: data.orderNotes,
        contactId: data.contact?.id ?? null,
        items,
        fileIds: fulfillmentFileUploads.getCompletedFileIds(),
      });
    },
    [createFulfillmentMutation, fulfillmentFileUploads]
  );

  // Handle return form submission
  const handleReturnSubmit = useCallback(
    (data: ReturnOrderFormData) => {
      const items = data.items.map((item) => ({
        itemId: item.item?.id ?? '',
        requestedQuantity: item.quantity ?? 0,
      }));

      createReturnMutation.mutate({
        returnType: data.returnType!,
        returnReason: data.returnReason,
        fromLocationId: data.fromLocation?.id ?? null,
        toLocationId: data.toLocation?.id ?? '',
        promiseShipDate: data.promiseShipDate,
        promiseDeliveryDate: data.promiseDeliveryDate,
        orderNotes: data.orderNotes,
        transportReferenceDetails: toTransportReferenceDetailsRequest(
          data.carrier,
          data.otherCarrier,
          data.referenceNumber
        ),
        items,
        fileIds: returnOrderFileUploads.getCompletedFileIds(),
      });
    },
    [createReturnMutation, returnOrderFileUploads]
  );

  // Exchange wizard handlers
  const handleExchangeFulfillmentData = useCallback(
    (data: FulfillmentOrderFormData) => {
      setFulfillmentOrderData(data);
      // Only set initial RO data on first transition to RO step
      setInitialReturnOrderData((prev) => {
        if (prev !== null) return prev; // Already initialized, don't overwrite
        // Increment key to force RO form remount with new initial data
        setRoFormKey((k) => k + 1);
        return {
          fromLocation: data.toLocation,
          toLocation: data.fromLocation,
          items: data.items, // Copy items from FO
          returnType: null,
          returnReason: null,
          promiseShipDate: null,
          promiseDeliveryDate: null,
          orderNotes: null,
          carrier: null,
          referenceNumber: null,
          otherCarrier: null,
        };
      });
      setExchangeStep(1); // Advance to RO step
    },
    []
  );

  const handleExchangeReturnData = useCallback((data: ReturnOrderFormData) => {
    setReturnOrderData(data);
    setExchangeStep(2); // Advance to review step
  }, []);

  const handleExchangeBack = useCallback(() => {
    setExchangeStep((prev) => Math.max(0, prev - 1));
  }, []);

  const handleExchangeSubmit = useCallback(() => {
    if (
      !returnOrderData ||
      !fulfillmentOrderData ||
      !returnOrderData.returnType ||
      !returnOrderData.toLocation ||
      !fulfillmentOrderData.fromLocation ||
      !fulfillmentOrderData.toLocation
    ) {
      return;
    }

    const request: CreateExchangeOrderRequest = {
      returnOrder: {
        returnType: returnOrderData.returnType,
        returnReason: returnOrderData.returnReason,
        fromLocationId: returnOrderData.fromLocation?.id ?? null,
        toLocationId: returnOrderData.toLocation.id,
        promiseShipDate: returnOrderData.promiseShipDate,
        promiseDeliveryDate: returnOrderData.promiseDeliveryDate,
        orderNotes: returnOrderData.orderNotes,
        transportReferenceDetails: toTransportReferenceDetailsRequest(
          returnOrderData.carrier,
          returnOrderData.otherCarrier,
          returnOrderData.referenceNumber
        ),
        items: returnOrderData.items.map((item) => ({
          itemId: item.item?.id ?? '',
          requestedQuantity: item.quantity ?? 0,
        })),
        fileIds: returnOrderFileUploads.getCompletedFileIds(),
      },
      fulfillmentOrder: {
        projectId: fulfillmentOrderData.project?.id ?? null,
        fromLocationId: fulfillmentOrderData.fromLocation.id,
        toLocationId: fulfillmentOrderData.toLocation.id,
        promiseShipDate: fulfillmentOrderData.promiseShipDate,
        promiseDeliveryDate: fulfillmentOrderData.promiseDeliveryDate,
        orderNotes: fulfillmentOrderData.orderNotes,
        contactId: fulfillmentOrderData.contact?.id ?? null,
        items: fulfillmentOrderData.items.map((item) => ({
          itemId: item.item?.id ?? '',
          requestedQuantity: item.quantity ?? 0,
        })),
        fileIds: fulfillmentFileUploads.getCompletedFileIds(),
      },
    };

    createExchangeMutation.mutate(request);
  }, [
    returnOrderData,
    fulfillmentOrderData,
    createExchangeMutation,
    returnOrderFileUploads,
    fulfillmentFileUploads,
  ]);

  // Render exchange review step
  const renderExchangeReview = () => {
    if (!returnOrderData || !fulfillmentOrderData) {
      return <Typography color="error">Missing form data</Typography>;
    }

    // Build merged items summary
    const itemsMap = new Map<
      string,
      {
        id: string;
        name: string;
        fulfilledQty: number | null;
        returnedQty: number | null;
      }
    >();

    for (const item of fulfillmentOrderData.items) {
      if (item.item) {
        const existing = itemsMap.get(item.item.id);
        if (existing) {
          existing.fulfilledQty =
            (existing.fulfilledQty ?? 0) + (item.quantity ?? 0);
        } else {
          itemsMap.set(item.item.id, {
            id: item.item.id,
            name: item.item.name,
            fulfilledQty: item.quantity ?? 0,
            returnedQty: null,
          });
        }
      }
    }

    for (const item of returnOrderData.items) {
      if (item.item) {
        const existing = itemsMap.get(item.item.id);
        if (existing) {
          existing.returnedQty =
            (existing.returnedQty ?? 0) + (item.quantity ?? 0);
        } else {
          itemsMap.set(item.item.id, {
            id: item.item.id,
            name: item.item.name,
            fulfilledQty: null,
            returnedQty: item.quantity ?? 0,
          });
        }
      }
    }

    const mergedItems = Array.from(itemsMap.values());

    return (
      <Stack spacing={3}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
            Exchange items summary
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Part or product</TableCell>
                <TableCell align="right">Quantity fulfilled</TableCell>
                <TableCell align="right">Quantity returned</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mergedItems.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.name}</TableCell>
                  <TableCell align="right">
                    {item.fulfilledQty ?? '—'}
                  </TableCell>
                  <TableCell align="right">{item.returnedQty ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
            Ready to create exchange
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Click "Add exchange" to create a fulfillment order and a return
            order and link them automatically
          </Typography>
        </Paper>
      </Stack>
    );
  };

  // Render exchange confirmation step
  const renderExchangeConfirmation = () => {
    if (!createdExchangeOrder) {
      return <Typography color="error">Missing exchange data</Typography>;
    }

    const linkedFO = createdExchangeOrder.fulfillmentOrders[0];
    const linkedRO = createdExchangeOrder.returnOrders[0];

    return (
      <Alert
        severity="success"
        icon={<CheckCircle />}
        sx={{ '& .MuiAlert-message': { width: '100%' } }}
      >
        <Typography variant="subtitle1" fontWeight="medium" mb={1}>
          Exchange {createdExchangeOrder.exchangeNumber} created successfully
        </Typography>

        <Stack direction="row" spacing={2}>
          {linkedFO && (
            <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Fulfillment order
              </Typography>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Link
                  to="/fulfillments/$fulfillmentKey"
                  params={{ fulfillmentKey: linkedFO.key }}
                  target="_blank"
                >
                  <Typography variant="subtitle1" color="primary">
                    {linkedFO.fulfillmentOrderNumber}
                  </Typography>
                </Link>
                <OrderNumberCopyButton
                  orderNumber={linkedFO.fulfillmentOrderNumber}
                />
              </Stack>
            </Paper>
          )}
          {linkedRO && (
            <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Return order
              </Typography>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Link
                  to="/return-orders/$returnOrderKey"
                  params={{ returnOrderKey: linkedRO.key }}
                  target="_blank"
                >
                  <Typography variant="subtitle1" color="primary">
                    {linkedRO.returnOrderNumber}
                  </Typography>
                </Link>
                <OrderNumberCopyButton
                  orderNumber={linkedRO.returnOrderNumber}
                />
              </Stack>
            </Paper>
          )}
        </Stack>
      </Alert>
    );
  };

  // Render content based on order type
  const renderContent = () => {
    if (orderType === 'fulfill') {
      return (
        <FulfillmentOrderForm
          key={`fulfill-${resetKey}`}
          initialProjectID={props.initialProjectID}
          disableProjectChange={props.disableProjectChange}
          orgDefaultOriginLocation={user.organization.defaultOriginLocation}
          onSubmit={handleFulfillmentSubmit}
          onProjectContextBlockedChange={setIsFulfillmentContextBlocked}
          formId={FORM_IDS.fulfill}
          fileUploadProps={{
            files: fulfillmentFileUploads.files,
            onFilesSelected: fulfillmentFileUploads.addFiles,
            onRemoveFile: fulfillmentFileUploads.removeFile,
            disabled: isPending,
          }}
        />
      );
    }

    if (orderType === 'return') {
      return (
        <ReturnOrderForm
          key={`return-${resetKey}`}
          onSubmit={handleReturnSubmit}
          orgDefaultReturnLocation={user.organization.defaultReturnLocation}
          formId={FORM_IDS.return}
          fileUploadProps={{
            files: returnOrderFileUploads.files,
            onFilesSelected: returnOrderFileUploads.addFiles,
            onRemoveFile: returnOrderFileUploads.removeFile,
            disabled: isPending,
          }}
        />
      );
    }

    // Exchange order - multi-step wizard
    const isConfirmationStep = exchangeStep === 3;

    return (
      <>
        <Box sx={{ mb: 3 }}>
          <Stepper
            alternativeLabel
            activeStep={
              isConfirmationStep ? EXCHANGE_STEPS.length : exchangeStep
            }
            sx={{
              '& .MuiStepIcon-root': { fontSize: '2rem' },
              '& .MuiStepIcon-text': { fontWeight: 'bold' },
              '& .MuiStepConnector-line': { borderTopWidth: '1px' },
              '& .MuiStepConnector-root': { top: '16px' },
            }}
          >
            {EXCHANGE_STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Box sx={{ maxHeight: '50vh', overflow: 'auto', pt: 1 }}>
          {/* Keep forms mounted but hidden to preserve state when navigating back/forward */}
          <Box sx={{ display: exchangeStep === 0 ? 'block' : 'none' }}>
            <FulfillmentOrderForm
              key={`exchange-fulfillment-${resetKey}`}
              formId={FORM_IDS.exchangeFulfillment}
              onSubmit={handleExchangeFulfillmentData}
              initialFormData={fulfillmentOrderData ?? undefined}
              initialProjectID={props.initialProjectID}
              disableProjectAutoFill
              orgDefaultOriginLocation={user.organization.defaultOriginLocation}
              fileUploadProps={{
                files: fulfillmentFileUploads.files,
                onFilesSelected: fulfillmentFileUploads.addFiles,
                onRemoveFile: fulfillmentFileUploads.removeFile,
                disabled: isPending,
              }}
            />
          </Box>
          <Box sx={{ display: exchangeStep === 1 ? 'block' : 'none', px: 2 }}>
            <ReturnOrderForm
              key={`exchange-return-${resetKey}-${roFormKey}`}
              formId={FORM_IDS.exchangeReturn}
              onSubmit={handleExchangeReturnData}
              initialFormData={
                returnOrderData ?? initialReturnOrderData ?? undefined
              }
              orgDefaultReturnLocation={user.organization.defaultReturnLocation}
              fileUploadProps={{
                files: returnOrderFileUploads.files,
                onFilesSelected: returnOrderFileUploads.addFiles,
                onRemoveFile: returnOrderFileUploads.removeFile,
                disabled: isPending,
              }}
            />
          </Box>
          {exchangeStep === 2 && renderExchangeReview()}
          {exchangeStep === 3 && renderExchangeConfirmation()}
        </Box>
      </>
    );
  };

  const canSubmitFulfillment =
    !isPending &&
    !isFulfillmentContextBlocked &&
    !fulfillmentFileUploads.isAnyUploading &&
    !fulfillmentFileUploads.hasFailedUploads;

  const canSubmitReturn =
    !isPending &&
    !returnOrderFileUploads.isAnyUploading &&
    !returnOrderFileUploads.hasFailedUploads;

  const canSubmitExchange =
    !isPending &&
    !fulfillmentFileUploads.isAnyUploading &&
    !fulfillmentFileUploads.hasFailedUploads &&
    !returnOrderFileUploads.isAnyUploading &&
    !returnOrderFileUploads.hasFailedUploads;

  // Render dialog actions based on order type and state
  const renderActions = () => {
    if (orderType === 'fulfill') {
      return (
        <>
          <Button onClick={props.onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={FORM_IDS.fulfill}
            variant="contained"
            color="primary"
            disabled={!canSubmitFulfillment}
            startIcon={
              isPending || fulfillmentFileUploads.isAnyUploading ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            Add fulfillment order
          </Button>
        </>
      );
    }

    if (orderType === 'return') {
      return (
        <>
          <Button onClick={props.onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={FORM_IDS.return}
            variant="contained"
            color="primary"
            disabled={!canSubmitReturn}
            startIcon={
              isPending || returnOrderFileUploads.isAnyUploading ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            Add return order
          </Button>
        </>
      );
    }

    // Exchange order actions
    const isReviewStep = exchangeStep === EXCHANGE_STEPS.length - 1;
    const isConfirmationStep = exchangeStep === 3;

    return (
      <Stack direction="row" justifyContent="space-between" width="100%">
        <Button
          onClick={handleExchangeBack}
          disabled={isPending || exchangeStep === 0 || isConfirmationStep}
        >
          Back
        </Button>
        <Stack direction="row" spacing={1}>
          {!isConfirmationStep && (
            <Button onClick={props.onClose} disabled={isPending}>
              Cancel
            </Button>
          )}
          {isConfirmationStep ? (
            <Button variant="contained" onClick={props.onClose}>
              Done
            </Button>
          ) : isReviewStep ? (
            <Button
              variant="contained"
              onClick={handleExchangeSubmit}
              disabled={!canSubmitExchange}
              startIcon={
                isPending ||
                fulfillmentFileUploads.isAnyUploading ||
                returnOrderFileUploads.isAnyUploading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
            >
              {isPending ? 'Creating...' : 'Add exchange'}
            </Button>
          ) : (
            <Button
              type="submit"
              form={
                exchangeStep === 0
                  ? FORM_IDS.exchangeFulfillment
                  : FORM_IDS.exchangeReturn
              }
              variant="contained"
              disabled={isPending}
            >
              Next
            </Button>
          )}
        </Stack>
      </Stack>
    );
  };

  // Get error message for snackbar based on order type
  const getSnackbarMessage = () => {
    switch (orderType) {
      case 'fulfill':
        return 'An error occurred creating the fulfillment order.';
      case 'return':
        return 'An error occurred creating the return order.';
      case 'exchange':
        return 'An error occurred creating the exchange.';
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={props.isOpen}
      onClose={props.onClose}
      scroll="paper"
      TransitionProps={{
        onExited: handleAfterClose,
      }}
    >
      <DialogTitle>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6" component="span">
            {TITLES[orderType]}
          </Typography>
          <FilterToggleGroup
            value={orderType}
            options={ORDER_TYPE_OPTIONS}
            onChange={handleOrderTypeChange}
            ariaLabel="Order type"
          />
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          {errorMessage && (
            <Alert severity="error" onClose={() => setErrorMessage(null)}>
              {errorMessage}
            </Alert>
          )}

          {renderContent()}
        </Stack>
      </DialogContent>
      <DialogActions>{renderActions()}</DialogActions>
      <SupportSnackbar
        isOpen={showSnackbar}
        onClose={() => {
          setShowSnackbar(false);
          setErrorMessage(null);
        }}
        message={getSnackbarMessage()}
      />
    </Dialog>
  );
}
